import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function AdminTax() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="The Admin Tax: What 5 Disconnected Tools Actually Cost a Roofing Company"
            date="March 20, 2026"
            readTime="7 MIN"
            heroImage="/images/blog/admin-tax-hero.jpg"
            description="You're running 6 pieces of software. Your competitors are running 1. The difference isn't efficiency — it's how much revenue bleeds out of your business every single day."
        >
            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                You're running 6 pieces of software. Your competitors are running 1.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Here's how the average roofing company operates: JobNimbus for the CRM. Google Sheets for scheduling. QuickBooks for invoicing. A separate estimating tool. A group text thread for crew coordination. And Gmail sitting in the middle, being used as an informal task manager because nothing else is connected to anything else.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Every one of those tools does its job. None of them talk to each other. Which means every time something moves from one step to the next, a human has to carry it.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's the admin tax. And you're paying it every day, whether you realize it or not.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What the tax actually costs</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Industry surveys put the number at 40% of potential revenue lost to context-switching, data entry, and admin bottlenecks. That's not a rounding error. That's 4 jobs out of every 10 going sideways because someone was entering a lead into a spreadsheet instead of calling it back.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                There's a simpler way to see it. Count the manual handoffs in your operation: lead comes in, someone logs it. Job gets scheduled, someone texts the crew. Invoice goes out, someone copies the amount from the estimate. Estimate gets updated, someone remembers to update the CRM. Each of those is 5 to 15 minutes. Most companies have 8 to 12 of these handoffs per job.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                At 10 jobs a week, that's 12 to 25 hours of pure transfer work. No value added. Just moving information from one tool to another by hand.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                25 hours a week. That's a part-time employee. One you're already paying for — in lost time.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">How you ended up here</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Nobody woke up and decided to build a 6-tool operation. It happened one subscription at a time.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Year one: QuickBooks, because the accountant said so. Year two: a CRM, because leads were slipping. Year three: estimating software, because the manual quotes were taking too long. Year four: scheduling software, because Google Calendar wasn't cutting it. By year five, you have $600 a month in SaaS subscriptions and a full-time admin position created specifically to move data between systems that were never designed to work together.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Each tool solved a real problem when you bought it. The problem is the gaps between them. The CRM doesn't know when a job is complete. The scheduler doesn't know when the estimate was accepted. QuickBooks doesn't know when the crew has been paid. Nothing in your operation has the full picture at any given moment.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That gap is where your admin tax lives.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What the tax looks like in spring</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Normal week: the gaps are annoying but manageable. Spring surge: the gaps eat you alive.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A hail event hits your market on a Wednesday. By Thursday morning, 67 people have tried to reach you. 17 voicemails. 9 emails. 22 website form submissions. Texts to three different numbers. A few DMs on Facebook that nobody checked.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                One person at the desk. She's also scheduling Thursday crews, handling a materials order that didn't arrive, and dealing with an insurance adjuster who keeps calling about a job from two weeks ago.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                By noon, she's called back 24 of the 67. Of those, 8 already went with someone else. Of the 24 reached, 14 are scheduled. The other 43 are still in voicemail purgatory.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's not a staffing failure. That's what happens when your operation is built for 15 contacts a week and you get 67 in 12 hours. The admin tax doesn't show up clearly until the system runs at 4x capacity. Then it's obvious.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What zero-tax operations look like</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Same storm. Same 67 contacts.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                In a zero-tax operation, 63 of those 67 get an acknowledgment within 3 minutes. Not a robocall. A real response with the homeowner's name, confirmation that their message came through, and a direct link to book an inspection. No one typed it. The system recognized the contact, pulled the context, and sent the reply.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                By the time the office opens Thursday morning, 34 inspections are already scheduled. The desk admin doesn't spend the day catching up on voicemails. She spends it confirming jobs, prepping crew assignments, and handling the exceptions that actually require a human judgment call.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The tools are still there. QuickBooks, CRM, scheduler. But they're not siloed anymore. When a lead converts to a job, the CRM updates automatically. When the job gets scheduled, the crew gets notified. When the invoice goes out, QuickBooks already has the line items. The handoffs are still happening. A human just isn't doing them.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's not science fiction. It's what an agent layer on top of your existing tools actually does.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The business math</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                If you recover 20% of your admin time, you haven't just saved some hours. You've effectively added a part-time operations coordinator to your business without hiring one.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                At 10 jobs a week, that 20% is roughly 5 hours back. That's 20 hours a month. At $25 an hour for an admin, you're looking at $500 in recovered capacity every month. Most roofing companies running this volume are spending $1,200 to $2,000 a month on admin already. Recovering 20% of that time pays for the solution and then some.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                But the real number isn't the admin savings. It's the jobs you stop losing. If 43 contacts go unreached during a surge, and you close your normal 40%, you've missed 17 jobs. At $8,000 average ticket, that's $136,000 in a single storm event. Not all of that is recoverable. But if you recover even a quarter of it with better systems, that's $34,000 you didn't have before.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                The admin tax is expensive when things are slow. During spring peak, it's catastrophic.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">One agent. Not six tools.</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                You don't need to rip out what you have. The CRM can stay. QuickBooks can stay. The estimating tool can stay.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                What changes is the layer between them. One agent that knows what's happening across your operation, moves information without being asked, and handles the contacts that currently go into voicemail purgatory. Your team focuses on the work that actually needs them. The agent handles the rest.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                This is not a long implementation project. It's not a six-month software migration. It's delivered, configured for your specific tools, and running in days. Not months.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Spring is already in motion. The next storm doesn't care whether your tools are connected. But it will show you exactly what the admin tax costs.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <div className="text-center my-12">
                <a
                    href="https://roofclaw.com"
                    className="inline-block bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-bold px-8 py-4 rounded-xl transition-colors text-lg"
                >
                    Book a 20-minute call
                </a>
            </div>

            <p className="text-[#F0EFF4]/60 text-base leading-relaxed">
                RoofClaw is an AI operations agent built for roofing companies. Delivered, configured, and running in days. No IT department required.
            </p>
        </BlogLayout>
    );
}
